// One trial API for every procedure kind; the server reads the kind off the order.
import { asMemberId, asObservationId, asOrderId, asPlannedTrialId, type MemberId } from "@repo/domain";
import { z } from "zod";
import { memberIdSchema as memberIdPattern } from "./common.schemas";
import { newEntityIdSchema } from "./entity-id.schemas";
import { orderSetIdSchema } from "./order-set.schemas";
import { patientDocumentIdSchema } from "./patient-document.schemas";
import { parameterAnswerSchema } from "./protocol-definition.schemas";
import { treatmentAnsweredValueSchema } from "./treatment-procedure.schemas";

const domainIdSchema = z.string().trim().min(1);
const memberIdSchema: z.ZodType<MemberId, z.ZodTypeDef, string> = z.string().trim().min(1).transform(asMemberId);

export const recordProcedureTrialInputSchema = z.object({
  procedureId: newEntityIdSchema,
  orderSetId: orderSetIdSchema,
  orderId: domainIdSchema.transform(asOrderId),
  id: newEntityIdSchema.transform(asObservationId),
  plannedTrialId: domainIdSchema.transform(asPlannedTrialId).optional(),
  effectiveDateTime: z.string().datetime({ offset: true }).optional(),
  /** The imported document a trial was read from. */
  derivedFromDocumentId: patientDocumentIdSchema.optional(),
  values: z.array(
    z.object({
      answer: parameterAnswerSchema.extend({
        source: z.union([z.literal("self"), memberIdPattern.transform(asMemberId)]),
        parameterId: memberIdSchema,
        value: treatmentAnsweredValueSchema,
      }),
      source: z.enum(["ordered", "entered"]),
    }),
  ),
});
export type RecordProcedureTrialInput = z.infer<typeof recordProcedureTrialInputSchema>;
/** Perform all: every trial lands in one commit, or none does. */
export const recordProcedureTrialsInputSchema = recordProcedureTrialInputSchema
  .pick({ procedureId: true, orderSetId: true, orderId: true })
  .extend({
    trials: z
      .array(recordProcedureTrialInputSchema.omit({ procedureId: true, orderSetId: true, orderId: true }))
      .min(1),
  });
export type RecordProcedureTrialsInput = z.infer<typeof recordProcedureTrialsInputSchema>;
export const correctProcedureTrialInputSchema = recordProcedureTrialInputSchema
  .omit({ derivedFromDocumentId: true })
  .extend({
    effectiveDateTime: z.string().datetime({ offset: true }),
    /** Omitted keeps the trial's link to its planned trial, null clears it. */
    plannedTrialId: domainIdSchema.transform(asPlannedTrialId).nullable().optional(),
  });
export type CorrectProcedureTrialInput = z.infer<typeof correctProcedureTrialInputSchema>;
export const retractProcedureTrialInputSchema = recordProcedureTrialInputSchema.pick({
  orderSetId: true,
  orderId: true,
  id: true,
});
export type RetractProcedureTrialInput = z.infer<typeof retractProcedureTrialInputSchema>;
